<template>
  <div id="forecast-report" class="fill-height align-start">
    <i-overlay v-if="initialLoading" text="Loading your forecast..." />

    <v-row v-else class="g-container" no-gutters>
      <v-col>
        <v-row class="ma-0 align-center justify-space-between">
          <h2>{{ $route.meta.title }}</h2>
          <v-btn
            small
            color="secondary"
            class="p-no-print"
            :loading="exportingPdf"
            :disabled="!isForecastReady"
            @click="exportPdf"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Export PDF
          </v-btn>
        </v-row>

        <v-alert v-if="errorMessage" class="mt-3" dense outlined type="error">
          {{ errorMessage }}
        </v-alert>

        <v-alert
          v-if="isForecastPartial"
          class="mt-3"
          dense
          outlined
          type="info"
        >
          Your full forecast is still syncing. Summary data is ready now, and
          the rest of the report will load automatically.
        </v-alert>

        <i-progress-linear v-if="loading" class="mt-3 p-no-print" />

        <v-row>
          <v-col :cols="12" :md="showForecastEmailCard ? 6 : 12" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-storefront-outline</v-icon>
                <span class="g-heading-xs g-bold"
                  >Supplier Sales History Insights - Period (Previous 3
                  Months)</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('supplierForecast')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <template v-if="forecast">
                <div class="mb-3">
                  <div class="g-body-2 g-medium-emphasis">Supplier</div>
                  <div class="g-heading-xs g-bold">
                    {{ forecast.supplier.name || forecast.supplier.id }}
                  </div>
                </div>

                <div class="mb-3">
                  <div class="g-body-2 g-medium-emphasis">Analysis period</div>
                  <div class="g-heading-xs g-bold">
                    {{ analysisDateLabel }}
                  </div>
                </div>

                <div class="p-stat-grid">
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      First Order Placed
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatDateTime(forecast.summary.firstSupplierOrderAt)
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Last Order Placed
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ formatDateTime(forecast.summary.lastSupplierOrderAt) }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Orders Analysed (Previous 3 Months)
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.summary.totalSupplierOrdersAnalysed }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Total Order Value (Previous 3 Months)
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.summary.totalOrderValuePreviousThreeMonths
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Average Monthly Order Value
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.summary
                            .averageMonthlyOrderValuePreviousThreeMonths
                        )
                      }}
                    </div>
                  </div>
                </div>

                <div class="mt-3 g-heading-xs g-bold p-section-header">
                  Current Month
                </div>
                <div class="p-stat-grid mt-2">
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Last Order Date (This Month)
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatDateTime(
                          forecast.summary.currentMonthLastSupplierOrderAt
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      No. of Orders (This Month)
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.summary.currentMonthOrderCount || 0 }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Total Sales Month to date (Day
                      {{ forecast.summary.currentMonthDayOfMonth || 0 }})
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(forecast.summary.currentMonthSalesToDate)
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Compared to Previous Months Average
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrencyWithSign(
                          forecast.summary
                            .currentMonthSalesDeltaVsPreviousThreeMonthAverage
                        )
                      }}
                    </div>
                  </div>
                </div>

                <div
                  v-if="
                    forecast &&
                    isSectionReady('turnInActivity') &&
                    forecast.turnInActivity
                  "
                  class="mt-4"
                >
                  <div class="g-body-2 g-medium-emphasis mb-2">
                    Turn In Order Activity (Last 3 Months)
                  </div>
                  <div class="p-stat-grid">
                    <div class="p-stat">
                      <div class="g-body-2 g-medium-emphasis">
                        Turn-in orders
                      </div>
                      <div class="g-heading-xs g-bold">
                        {{ forecast.turnInActivity.totalTurnInOrders }}
                      </div>
                    </div>
                    <div class="p-stat">
                      <div class="g-body-2 g-medium-emphasis">
                        Total Turn-In Order Qty
                        <v-icon
                          x-small
                          class="ml-1 p-help-icon"
                          v-tooltip="logicText('turnInOrderQty')"
                          >mdi-help-circle-outline</v-icon
                        >
                      </div>
                      <div class="g-heading-xs g-bold">
                        {{ forecast.turnInActivity.totalTurnInQuantity }}
                      </div>
                    </div>
                    <div class="p-stat">
                      <div class="g-body-2 g-medium-emphasis">
                        Total Retailers with Turn In Orders
                      </div>
                      <div class="g-heading-xs g-bold">
                        {{ forecast.turnInActivity.turnInStoreCount }}
                      </div>
                    </div>
                    <div class="p-stat">
                      <div class="g-body-2 g-medium-emphasis">
                        Days since last Turn In Order
                      </div>
                      <div class="g-heading-xs g-bold">
                        {{
                          daysSince(forecast.turnInActivity.latestTurnInOrderAt)
                        }}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="forecast && isSectionPending('turnInActivity')"
                  class="mt-4 g-body-1"
                >
                  Turn-in activity is still syncing and will appear
                  automatically.
                </div>
              </template>
              <div v-else class="g-body-1">
                No supplier forecast data available.
              </div>
            </v-card>
          </v-col>

          <v-col v-if="showForecastEmailCard" cols="12" md="6" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-email-fast-outline</v-icon>
                <span class="g-heading-xs g-bold">Forecast Emails</span>
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('forecastEmails')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <template v-if="forecast && forecast.emailPreview">
                <div class="mb-2 g-body-2 g-medium-emphasis">
                  Reminder preview for {{ forecast.emailPreview.supplierName }}
                </div>
                <ul class="pl-4">
                  <li>
                    Days since last order:
                    {{ formatDays(forecast.emailPreview.daysSinceLastOrder) }}
                  </li>
                  <li>
                    Average order gap:
                    {{ formatDays(forecast.emailPreview.averageOrderGapDays) }}
                  </li>
                  <li>
                    Top customer count:
                    {{ forecast.emailPreview.topCustomers.length }}
                  </li>
                </ul>
              </template>
              <div v-else class="g-body-1">
                No forecast email preview available.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-account-alert-outline</v-icon>
                <span class="g-heading-xs g-bold"
                  >Customers Who Have Not Ordered In Last 3 Months (But Ordered
                  Previously)</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('missedCycleCustomers')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <v-simple-table
                dense
                v-if="
                  forecast &&
                  forecast.inactivePreviousCustomers &&
                  forecast.inactivePreviousCustomers.length
                "
              >
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Customer Code</th>
                    <th>Last Order</th>
                    <th>Days Since</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="customer in forecast.inactivePreviousCustomers"
                    :key="`inactive-row2-${customer.storeId}`"
                  >
                    <td>{{ customerDisplayName(customer) }}</td>
                    <td>{{ customerDisplayCode(customer) }}</td>
                    <td>{{ formatDate(customer.lastOrderAt) }}</td>
                    <td>{{ formatDays(customer.daysSinceLastOrder) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-else-if="
                  forecast && isSectionPending('inactivePreviousCustomers')
                "
                class="g-body-1"
              >
                Inactive customer insights are still syncing.
              </div>
              <div v-else class="g-body-1">
                No inactive previous customers found.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-chart-line</v-icon>
                <span class="g-heading-xs g-bold">Monthly Sales</span>
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('monthlySales')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <template
                v-if="
                  forecast &&
                  isSectionReady('monthlySales') &&
                  forecast.monthlySales
                "
              >
                <div class="p-stat-grid">
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Last month sales
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ formatCurrency(forecast.monthlySales.lastMonthSales) }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      This month sales so far
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.monthlySales.currentMonthSalesSoFar
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Predicted this month sales
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.monthlySales.predictedCurrentMonthSales
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">Prediction gap</div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(forecast.monthlySales.predictionDelta)
                      }}
                    </div>
                  </div>
                </div>
                <div class="g-body-2 g-medium-emphasis mt-3">
                  Prediction uses day
                  {{ forecast.monthlySales.currentMonthDayOfMonth }} of
                  {{ forecast.monthlySales.currentMonthDaysInMonth }} in the
                  current month.
                </div>
              </template>
              <div
                v-else-if="forecast && isSectionPending('monthlySales')"
                class="g-body-1"
              >
                Monthly sales insights are still syncing.
              </div>
              <div v-else class="g-body-1">
                No monthly sales forecast available.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-history</v-icon>
                <span class="g-heading-xs g-bold"
                  >Last 6 Months Sales (Inc Freight & Tax)</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('sixMonthSales')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <div
                v-if="
                  forecast &&
                  isSectionReady('sixMonthSalesHistory') &&
                  forecast.sixMonthSalesHistory &&
                  forecast.sixMonthSalesHistory.length
                "
                class="p-monthly-sales-chart p-monthly-sales-chart-supplier"
              >
                <div
                  v-for="point in forecast.sixMonthSalesHistory"
                  :key="point.monthLabel"
                  class="p-sales-bar-col"
                >
                  <div class="p-sales-bars p-sales-bars-triple">
                    <div class="p-metric-col">
                      <div class="p-sales-bar-value">
                        {{ formatCurrency(point.sales) }}
                      </div>
                      <div class="p-sales-bar-track">
                        <div
                          class="p-sales-bar-fill"
                          :class="{
                            'p-sales-bar-fill-current': point.isCurrentMonth
                          }"
                          :style="{
                            height: `${Math.max(
                              6,
                              Math.round(
                                (Number(point.sales || 0) /
                                  maxSixMonthSalesValue) *
                                  100
                              )
                            )}%`
                          }"
                        ></div>
                      </div>
                      <div class="p-sales-order-label">Sales</div>
                    </div>
                    <div class="p-metric-col">
                      <div class="p-sales-bar-value">
                        {{ point.orderCount || 0 }}
                      </div>
                      <div class="p-order-bar-track">
                        <div
                          class="p-order-bar-fill"
                          :class="{
                            'p-order-bar-fill-current': point.isCurrentMonth
                          }"
                          :style="{
                            height: `${Math.max(
                              6,
                              Math.round(
                                (Number(point.orderCount || 0) /
                                  maxSixMonthOrderCount) *
                                  100
                              )
                            )}%`
                          }"
                        ></div>
                      </div>
                      <div class="p-sales-order-label">Orders</div>
                    </div>
                    <div class="p-metric-col">
                      <div class="p-sales-bar-value">
                        {{ formatCurrency(point.freightSales) }}
                      </div>
                      <div class="p-freight-bar-track">
                        <div
                          class="p-freight-bar-fill"
                          :class="{
                            'p-freight-bar-fill-current': point.isCurrentMonth
                          }"
                          :style="{
                            height: `${Math.max(
                              6,
                              Math.round(
                                (Number(point.freightSales || 0) /
                                  maxSixMonthFreightValue) *
                                  100
                              )
                            )}%`
                          }"
                        ></div>
                      </div>
                      <div class="p-sales-order-label">Freight</div>
                    </div>
                  </div>
                  <div class="p-sales-bar-label">
                    {{ point.monthLabel }}
                    <span v-if="point.isCurrentMonth">(current)</span>
                  </div>
                </div>
              </div>
              <div
                v-else-if="forecast && isSectionPending('sixMonthSalesHistory')"
                class="g-body-1"
              >
                Six-month sales history is still syncing.
              </div>
              <div v-else class="g-body-1">
                No six-month sales history available.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="7" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-account-group-outline</v-icon>
                <span class="g-heading-xs g-bold"
                  >Top Customers (Previous 3 Months)</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('topCustomers')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <v-simple-table
                dense
                v-if="
                  forecast &&
                  isSectionReady('topCustomers') &&
                  forecast.topCustomers &&
                  forecast.topCustomers.length
                "
              >
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Customer Code</th>
                    <th>Orders</th>
                    <th>Qty</th>
                    <th>Total Sales (3 Months)</th>
                    <th>This Month Sales</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="customer in forecast.topCustomers.slice(0, 5)"
                    :key="customer.storeId"
                  >
                    <td>{{ customerDisplayName(customer) }}</td>
                    <td>{{ customerDisplayCode(customer) }}</td>
                    <td>{{ customer.orderCount }}</td>
                    <td>{{ customer.totalQuantity }}</td>
                    <td>{{ formatCurrency(customer.totalSales) }}</td>
                    <td>{{ formatCurrency(customer.currentMonthSales) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-else-if="forecast && isSectionPending('topCustomers')"
                class="g-body-1"
              >
                Top customer insights are still syncing.
              </div>
              <div v-else class="g-body-1">No customer insights available.</div>
            </v-card>
          </v-col>

          <v-col cols="12" md="5" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-package-variant</v-icon>
                <span class="g-heading-xs g-bold">Best-Selling Products</span>
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('bestSellingProducts')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <v-simple-table
                dense
                v-if="
                  forecast &&
                  isSectionReady('bestSellingProducts') &&
                  forecast.bestSellingProducts &&
                  forecast.bestSellingProducts.length
                "
              >
                <thead>
                  <tr>
                    <th>Supplier Item Code</th>
                    <th>Description</th>
                    <th>Promotion</th>
                    <th>Orders</th>
                    <th>Qty</th>
                    <th>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="product in forecast.bestSellingProducts.slice(0, 5)"
                    :key="product.productCode"
                  >
                    <td>{{ productDisplayCode(product) }}</td>
                    <td>{{ productDisplayDescription(product) }}</td>
                    <td>
                      <div
                        class="p-tag-wrap"
                        v-if="product.promotionTypes.length"
                      >
                        <v-chip
                          v-for="tag in product.promotionTypes"
                          :key="`best-selling-tag-${product.productCode}-${tag}`"
                          x-small
                          :class="['p-compact-tag', promotionTagClass(tag)]"
                        >
                          {{ promotionTagLabel(tag) }}
                        </v-chip>
                      </div>
                      <span v-else>N/A</span>
                    </td>
                    <td>{{ product.orderCount }}</td>
                    <td>{{ product.totalQuantity }}</td>
                    <td>{{ formatCurrency(product.totalSales) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-else-if="forecast && isSectionPending('bestSellingProducts')"
                class="g-body-1"
              >
                Best-selling product insights are still syncing.
              </div>
              <div v-else class="g-body-1">No product insights available.</div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-account-star-outline</v-icon>
                <span class="g-heading-xs g-bold"
                  >Highest Purchasing Customers In Your State Not Purchasing
                  From This Supplier</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('customerOpportunities')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <v-simple-table
                dense
                v-if="
                  forecast &&
                  isSectionReady('topCustomerOpportunities') &&
                  forecast.topCustomerOpportunities &&
                  forecast.topCustomerOpportunities.length
                "
              >
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Code</th>
                    <th>Previous Period Sales</th>
                    <th>Opportunity</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="customer in forecast.topCustomerOpportunities"
                    :key="`opportunity-${customer.storeId}`"
                  >
                    <td>{{ customerDisplayName(customer) }}</td>
                    <td>{{ customerDisplayCode(customer) }}</td>
                    <td>{{ formatCurrency(customer.totalSales) }}</td>
                    <td>
                      {{ customer.opportunitySignal.level }} ·
                      {{ formatScore(customer.opportunitySignal.score) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-else-if="
                  forecast && isSectionPending('topCustomerOpportunities')
                "
                class="g-body-1"
              >
                Customer opportunity insights are still syncing.
              </div>
              <div v-else class="g-body-1">
                No high-purchase inactive customers available.
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="6" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-trending-up</v-icon>
                <span class="g-heading-xs g-bold"
                  >Products With No Sales Or Low Sales (Lowest 10)</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('predictedProducts')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <v-simple-table
                dense
                v-if="
                  forecast &&
                  isSectionReady('predictedBestSellingProducts') &&
                  forecast.predictedBestSellingProducts &&
                  forecast.predictedBestSellingProducts.length
                "
              >
                <thead>
                  <tr>
                    <th>Supplier Item Code</th>
                    <th>Description</th>
                    <th>Sales</th>
                    <th>Orders</th>
                    <th>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="product in forecast.predictedBestSellingProducts"
                    :key="`predicted-${product.productCode}`"
                  >
                    <td>{{ productDisplayCode(product) }}</td>
                    <td>{{ productDisplayDescription(product) }}</td>
                    <td>{{ formatCurrency(product.totalSales) }}</td>
                    <td>{{ product.orderCount }}</td>
                    <td>{{ product.totalQuantity }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div
                v-else-if="
                  forecast && isSectionPending('predictedBestSellingProducts')
                "
                class="g-body-1"
              >
                Low-selling product insights are still syncing.
              </div>
              <div v-else class="g-body-1">
                No low-selling products available.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class="d-flex">
            <v-card
              class="p-card flex-grow-1 px-4 px-md-6 pt-3 pt-md-4 pb-4 pb-md-6"
            >
              <v-row align="center">
                <v-icon class="p-icon">mdi-chart-bar</v-icon>
                <span class="g-heading-xs g-bold"
                  >Current Month Turn-In Comparison</span
                >
                <v-spacer />
                <v-icon
                  class="p-help-icon"
                  small
                  v-tooltip="logicText('turnInComparison')"
                  >mdi-help-circle-outline</v-icon
                >
              </v-row>
              <v-divider class="mt-2 mb-3" horizontal></v-divider>
              <template
                v-if="
                  forecast &&
                  isSectionReady('turnInComparison') &&
                  forecast.turnInComparison &&
                  forecast.turnInComparison.comparisonReady
                "
              >
                <div class="p-stat-grid">
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month turn-in orders
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.turnInComparison.linkedTurnInOrders }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month normal orders
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.turnInComparison.directOrders }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month turn-in sales
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.turnInComparison.linkedTurnInSales
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month normal sales
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(forecast.turnInComparison.directSales)
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month turn-in quantity
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.turnInComparison.linkedTurnInQuantity }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Current month normal quantity
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{ forecast.turnInComparison.directQuantity }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Sales difference
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatCurrency(
                          forecast.turnInComparison.salesDifference
                        )
                      }}
                    </div>
                  </div>
                  <div class="p-stat">
                    <div class="g-body-2 g-medium-emphasis">
                      Turn-in sales share
                    </div>
                    <div class="g-heading-xs g-bold">
                      {{
                        formatPercentage(
                          forecast.turnInComparison.linkedTurnInSalesPercentage
                        )
                      }}
                    </div>
                  </div>
                </div>
              </template>
              <template
                v-else-if="
                  forecast &&
                  isSectionReady('turnInComparison') &&
                  forecast.turnInComparison
                "
              >
                <div class="g-body-1">
                  Comparison will show once at least
                  {{
                    forecast.turnInComparison.minimumTurnInOrdersForComparison
                  }}
                  current-month turn-in orders are available.
                </div>
              </template>
              <div
                v-else-if="forecast && isSectionPending('turnInComparison')"
                class="g-body-1"
              >
                Current month turn-in comparison is still syncing.
              </div>
              <div v-else class="g-body-1">
                Current month turn-in comparison is not available yet.
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { formatUtcDateTime } from '@/utils/timezone'
import { mapActions, mapGetters } from 'vuex'
import iOverlay from '@/components/Overlay'
import iProgressLinear from '@/components/ProgressLinear'
import { fetchForecastPdf, fetchForecastReport } from '@/services/reports'

const FORECAST_LOGIC = {
  supplierForecast:
    'Sales History Insights reviews the previous 3 months of trade.',
  forecastEmails:
    'Builds reminder preview from the same supplier cycle metrics and top missed-cycle customers used by forecast scoring.',
  monthlySales:
    'Projected month sales = current month run-rate scaled by day-of-month with model-assisted trend weighting when available.',
  sixMonthSales:
    'Shows six-month sales, order counts, and freight totals with side-by-side bars. Freight uses order-level delivery-fee totals.',
  missedCycleRisk:
    'Ranks customers with risk score from missed reorder cadence: higher score means longer gap vs normal order cycle.',
  topCustomers:
    'Sorts customers by realised sales and order volume in the selected analysis window.',
  bestSellingProducts:
    'Sorts supplier products by historical sales contribution and quantity over the analysis window.',
  customerOpportunities:
    'Highlights customers that purchased strongly in the prior period but have no purchases in the current 3-month period.',
  predictedProducts:
    'Shows products with the lowest realised sales in the current 3-month period.',
  promotionUplift:
    'Flags products with strongest expected uplift if promoted, considering current sales baseline and promo context.',
  turnInComparison:
    'Compares current-month linked turn-in orders against current-month normal direct orders to show sales/quantity impact from turn-in-driven demand.',
  missedCycleCustomers:
    'Lists customers that ordered in the previous period but have not ordered in the latest 3-month period.',
  turnInOrderQty:
    'Total Turn-In Order Qty is the summed ordered quantity from turn-in items, not the number of order lines.'
}
const FORECAST_POLL_INTERVAL_MS = 5000

export default {
  components: {
    iOverlay,
    iProgressLinear
  },

  data: () => ({
    initialLoading: true,
    loading: false,
    errorMessage: '',
    forecast: null,
    forecastPollTimer: null,
    exportingPdf: false
  }),

  computed: {
    ...mapGetters('tenant', ['tenantLocale', 'tenantCurrency']),

    showForecastEmailCard() {
      return false
    },

    isForecastReady() {
      return this.forecast?.forecastStatus === 'READY'
    },

    isForecastPartial() {
      return this.forecast?.forecastStatus === 'PARTIAL'
    },

    analysisDateLabel() {
      if (!this.forecast) {
        return 'N/A'
      }

      const fallbackDateTo = moment
        .utc()
        .startOf('month')
        .subtract(1, 'millisecond')
      const dateTo =
        this.forecast.analysisDateTo || fallbackDateTo.toISOString()
      const dateFrom =
        this.forecast.analysisDateFrom ||
        moment.utc(dateTo).startOf('month').subtract(2, 'months').toISOString()

      return `${this.formatDate(dateFrom)} to ${this.formatDate(dateTo)}`
    },

    maxSixMonthSalesValue() {
      const points = this.forecast?.sixMonthSalesHistory || []
      const maxValue = points.reduce(
        (maxSales, point) => Math.max(maxSales, Number(point.sales || 0)),
        0
      )
      return Math.max(1, maxValue)
    },

    maxSixMonthOrderCount() {
      const points = this.forecast?.sixMonthSalesHistory || []
      const maxValue = points.reduce(
        (maxCount, point) => Math.max(maxCount, Number(point.orderCount || 0)),
        0
      )
      return Math.max(1, maxValue)
    },

    maxSixMonthFreightValue() {
      const points = this.forecast?.sixMonthSalesHistory || []
      const maxValue = points.reduce(
        (maxFreight, point) =>
          Math.max(maxFreight, Number(point.freightSales || 0)),
        0
      )
      return Math.max(1, maxValue)
    }
  },

  created() {
    this.loadForecast({ includeOptionalSections: false })
  },

  beforeUnmount() {
    this.stopForecastPolling()
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    logicText(section) {
      return (
        FORECAST_LOGIC[section] ||
        'Forecast explanation is not available for this section yet.'
      )
    },

    buildForecastPdfFilename() {
      return `forecast-supplier-${moment().format('YYYYMMDD-HHmm')}.pdf`
    },

    downloadPdf(blob, filename) {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(link.href)
    },

    isSectionReady(sectionName) {
      if (!this.forecast) {
        return false
      }

      return (
        this.forecast.forecastStatus === 'READY' ||
        this.forecast.completedSections?.includes(sectionName)
      )
    },

    isSectionPending(sectionName) {
      return this.forecast?.pendingSections?.includes(sectionName)
    },

    async loadForecast({ silent = false, includeOptionalSections } = {}) {
      if (!silent) {
        this.loading = true
      }
      this.errorMessage = ''

      try {
        this.forecast = await fetchForecastReport({ includeOptionalSections })
        if (this.isForecastPartial) {
          this.startForecastPolling()
        } else {
          this.stopForecastPolling()
        }
      } catch (error) {
        if (!silent) {
          this.errorMessage =
            error?.response?.data?.message ||
            'Unable to load forecast right now.'
        }
      } finally {
        if (!silent) {
          this.loading = false
        }
        this.initialLoading = false
      }
    },

    startForecastPolling() {
      if (this.forecastPollTimer) {
        return
      }

      this.forecastPollTimer = window.setInterval(() => {
        this.loadForecast({
          silent: true,
          includeOptionalSections: true
        })
      }, FORECAST_POLL_INTERVAL_MS)
    },

    stopForecastPolling() {
      if (!this.forecastPollTimer) {
        return
      }

      window.clearInterval(this.forecastPollTimer)
      this.forecastPollTimer = null
    },

    async exportPdf() {
      if (!this.isForecastReady) {
        this.addNotification({
          color: 'warning',
          text: 'PDF export will be available once the full forecast has synced.'
        })
        return
      }

      this.exportingPdf = true
      try {
        const { blob, filename } = await fetchForecastPdf()
        this.downloadPdf(blob, filename || this.buildForecastPdfFilename())
      } catch (error) {
        this.addNotification({
          color: 'error',
          text:
            error?.response?.data?.message ||
            'Unable to export forecast PDF right now.'
        })
      } finally {
        this.exportingPdf = false
      }
    },

    formatDateTime(value) {
      return formatUtcDateTime(value, 'DD MMM YYYY, h:mm A', 'N/A')
    },

    formatDate(value) {
      return value ? moment(value).format('DD MMM YYYY') : 'N/A'
    },

    formatDays(value) {
      return value === null || value === undefined ? 'N/A' : `${value} days`
    },

    daysSince(value) {
      if (!value) return 'N/A'
      const diffDays = moment().diff(moment(value), 'days')
      return `${Math.max(0, diffDays)} days`
    },

    formatCurrency(value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      return new Intl.NumberFormat(this.tenantLocale, {
        style: 'currency',
        currency: this.tenantCurrency
      }).format(Number(value))
    },

    formatCurrencyWithSign(value) {
      const numeric = Number(value)
      if (Number.isNaN(numeric)) return this.$options.filters.currency(0)
      const formatted = this.formatCurrency(Math.abs(numeric))
      return `${numeric >= 0 ? '+' : '-'} ${formatted}`
    },

    formatPercentage(value) {
      return value === null || value === undefined
        ? 'N/A'
        : `${Number(value).toFixed(2)}%`
    },

    customerDisplayName(customer) {
      return (
        customer.storeName ||
        customer.customerCode ||
        `Store ${customer.storeId}`
      )
    },

    customerDisplayCode(customer) {
      return customer.customerCode || String(customer.storeId)
    },

    productDisplayCode(product) {
      return product.supplierItemCode || product.productCode || 'N/A'
    },

    productDisplayDescription(product) {
      return product.productDescription || product.productCode || 'N/A'
    },

    promotionTagLabel(tag) {
      if (tag === 'BUY_GET') return 'BUY GET FREE'
      if (tag === 'VOLUME') return 'VOLUME BUY'
      if (tag === 'SPECIAL') return 'SPECIAL'
      return tag
    },

    promotionTagClass(tag) {
      if (tag === 'BUY_GET') return 'i-tag-buy-get'
      if (tag === 'VOLUME') return 'i-tag-volume-buy'
      if (tag === 'SPECIAL') return 'i-tag-special'
      return 'secondary'
    },

    formatScore(value) {
      return value === null || value === undefined
        ? 'N/A'
        : Number(value).toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
#forecast-report {
  .p-card .v-row .g-heading-xs.g-bold {
    color: #d32f2f;
  }

  .p-section-header {
    color: #d32f2f;
  }

  .p-icon {
    width: 32px;
    height: 32px;
    margin-left: 8px;
    margin-right: 8px;
    color: $secondary-color;
  }

  .p-stat-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .p-stat {
    padding: 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.03);
  }

  .p-help-icon {
    color: $secondary-color;
    cursor: help;
  }

  .p-tag-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
  }

  .p-compact-tag {
    height: 14px !important;
    min-height: 14px !important;
    padding: 0 4px !important;
    border-radius: 7px !important;
  }

  .p-compact-tag :deep(.v-chip__content) {
    font-size: 9px !important;
    font-weight: 600;
    line-height: 1 !important;
    letter-spacing: 0;
    padding: 0 !important;
  }

  .p-monthly-sales-chart {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
  }

  .p-monthly-sales-chart-supplier {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: 18px;
  }

  .p-sales-bar-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .p-sales-bar-value {
    font-size: 12px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
  }

  .p-sales-bars {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 8px;
  }

  .p-sales-bars-triple {
    gap: 10px;
  }

  .p-metric-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  .p-sales-bar-track {
    width: 30px;
    height: 140px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
    overflow: hidden;
    display: flex;
    align-items: flex-end;
  }

  .p-sales-bar-fill {
    width: 100%;
    background: $secondary-color;
    border-radius: 8px 8px 0 0;
  }

  .p-sales-bar-fill-current {
    background: $primary-color;
  }

  .p-order-bar-track {
    width: 30px;
    height: 140px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
    overflow: hidden;
    display: flex;
    align-items: flex-end;
  }

  .p-order-bar-fill {
    width: 100%;
    background: $primary-color;
    border-radius: 8px 8px 0 0;
  }

  .p-order-bar-fill-current {
    background: $secondary-color;
  }

  .p-freight-bar-track {
    width: 30px;
    height: 140px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.08);
    overflow: hidden;
    display: flex;
    align-items: flex-end;
  }

  .p-freight-bar-fill {
    width: 100%;
    background: rgba(0, 0, 0, 0.35);
    border-radius: 8px 8px 0 0;
  }

  .p-freight-bar-fill-current {
    background: rgba(0, 0, 0, 0.75);
  }

  .p-sales-order-label {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.7);
  }

  .p-sales-bar-label {
    text-align: center;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.7);
  }
}

@media only screen and (min-width: 600px) {
  #forecast-report {
    .p-card {
      min-height: 180px;
    }
  }
}

@media only screen and (max-width: 960px) {
  #forecast-report {
    .p-monthly-sales-chart {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .p-monthly-sales-chart-supplier {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .p-sales-bar-track {
      height: 110px;
    }

    .p-order-bar-track {
      height: 110px;
    }

    .p-freight-bar-track {
      height: 110px;
    }
  }
}
</style>
